import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { useAuth } from '../contexts/AuthContext.js';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Paginator } from 'primereact/paginator';
import { Calendar } from 'primereact/calendar';
import { ScrollTop } from 'primereact/scrolltop';
import { ProgressBar } from 'primereact/progressbar';
import useStatusPageScript from '../hooks/useStatusPageScript.js';
import { Tag } from 'primereact/tag';
import '../config.js'
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';
        

const ClientStatusLogs = () => {
  // Load the status page script
  useStatusPageScript();
  
  const { name, id } = useParams();
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    log: { value: null, matchMode: FilterMatchMode.CONTAINS },
    date: { value: null, matchMode: FilterMatchMode.CONTAINS},
    'client.name': { value: null, matchMode: FilterMatchMode.CONTAINS},
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [dataloaded, setDataLoaded] = useState('');
  const [showdebugmessage, setShowDebugMessage] = useState('');
  const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect({
    deniedTitle: 'Status Logs Access Denied',
    deniedMessage: 'You do not have permission to view client status logs for this record.',
  });

  useEffect(() => {
    const fetchEvents = async () => {
        getEvents(rows, 0);
    };

    fetchEvents();
  }, [id]);

  const formatQueryDate = (value) => new Date(value).toLocaleString('sv').replace(' ', 'T').split('T')[0];

  const getQueryDateRange = () => {
    const [start, end] = Array.isArray(dateRange) ? dateRange : [];
    const startDate = start || new Date();
    const endDate = end || startDate;

    return {
      initDate: formatQueryDate(startDate),
      endDate: formatQueryDate(endDate),
    };
  };

  const getEvents = async (rows, page) => {
    try {
        clearAccessIssue();
        setLoading(true);
        setEvents([]);
        const { initDate, endDate } = getQueryDateRange();
        setShowDebugMessage(`Fetching logs for client with ID ${id} from ${initDate} to ${endDate}`);

        console.log('date range:', initDate, endDate)
        const token = localStorage.getItem('token');
        const url = global.config.url + `/clientlogs/statuslog/${id}?page=${page}&pageSize=${rows}&initDate=${initDate}&endDate=${endDate}`;
        const response = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setEvents(response.data);
          if(response.status === 200){
            if(response.data.length > 0){
              for(let i = 0; i < response.data.length; i++){
                let date = new Date(response.data[i].date + 'Z');
                response.data[i].date = date.toLocaleString("en-GB");
              }
              setDataLoaded('Data loaded correctly');
            }
            else {
              setDataLoaded('No data found');
            }
            setLoading(false);
            const urlCount = global.config.url + `/clientlogs/statuslogcount/${id}?initDate=${initDate}&endDate=${endDate}`;
            const responseCount = await axios.get(urlCount, {
                headers: {
                Authorization: `Bearer ${token}`,
                },
            });
            setTotalRecords(responseCount.data);
        }
        else {
            setDataLoaded('No information from the server');
            setLoading(false);
            setTotalRecords(0);
            setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching event record:', error);
        setDataLoaded(error.message);
        handleAccessError(error);
          setLoading(false);
          setTotalRecords(0);
          setEvents([]);
      }
    };
  const onPageChange = (event) => {
    setLoading(true);
    setRows(event.rows);
    getEvents(event.rows, event.page);
    setFirst(event.first);
  };

  const getByDate = () =>{
    setLoading(true);
    setFirst(0);
    getEvents(rows, 0)
  }

const statusBodyTemplate = (client) => {
      return <Tag value={client.status.toString()} severity={getSeverity(client)}></Tag>;
  };

  const getSeverity = (client) => {
    switch (client.status) {
        case 'Online':
            return 'success';

        case 'Offline':
            return 'danger';

        default:
            return null;
    }
};

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters['global'].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const progressbar = (        
    <div className="card mt-3">
        <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>);

  if (accessIssue) {
    return <AccessState {...accessIssue} />;
  }

  return (
    <Card className='justify-content-md-center mt-5'>
    <Container fluid="md" >
    <div className="panel">
        <div className="flex align-content-center flex-wrap">
          <Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className='flex mb-3'/> 
          <h4 className='flex mt-2 mb-3'>Client Status logs for client {name}</h4> 
          <span className="p-input-icon-left ml-3 mb-3">
              <i className="pi pi-search" />
              <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Filter Search" tooltip="Start typing to filter" 
              tooltipOptions={{position: "bottom"}}/>
          </span>
          <div className="card flex justify-content-center ml-3 mb-3">
            <Calendar value={dateRange} onChange={(e) => setDateRange(e.value || [new Date(), new Date()])} selectionMode="range" dateFormat="dd/mm/yy" showIcon  showButtonBar readOnlyInput />
          </div>
          <div className="card flex justify-content-center ml-3 mb-3">
            <Button label="Get" className="p-button-raised" onClick={getByDate} />
          </div>
        </div>

      {/* <Toolbar           
      start={
} 
        end={          
            } /> */}
    </div>
    {loading && progressbar}

    {/* <Row className='justify-content-md-center mt-5'>
      <Col sm={8}>

      </Col>
      <Col sm={4}>

      </Col>
    </Row> */}

    
    {/* <Row className='justify-content-md-center mt-5'>
        <div className="card">
            <Paginator first={first} rows={rows} totalRecords={totalRecords} rowsPerPageOptions={[20, 30, 40, 50, 75, 100]} onPageChange={onPageChange} />
        </div>
    </Row> */}

    <Row className='justify-content-md-center mt-5'>
        <Col>
        {/* <h5 className='text-center'>Debug message: {showdebugmessage}</h5>
        <h5 className='text-center'>{dataloaded}</h5> */}
        <DataTable value={events} selectionMode={'radiobutton'} selection={rowSelectionModel} onSelectionChange={(e) => setRowSelectionModel(e.value.id)} 
        dataKey="id" tableStyle={{ minWidth: '50rem' }} filters={filters} filterDisplay="menu"
        globalFilterFields={['status']} emptyMessage={loading ? "Loading client logs..." :"No client logs found."}
        scrollable scrollHeight="60vh">
            <Column field="status" header="Status" body={statusBodyTemplate}></Column>
            <Column field="time" header="Time"></Column>
            <Column field="client.name" header="Client"></Column>
        </DataTable>
        </Col>

    </Row>

    <Row className='justify-content-md-center mt-5'>
        <div className="card">
            <Paginator first={first} rows={rows} totalRecords={totalRecords} rowsPerPageOptions={[20, 30, 40, 50, 75, 100]} onPageChange={onPageChange} />
        </div>
    </Row>

    </Container>
    <ScrollTop />
    </Card>
  );
};

export default ClientStatusLogs;
